import { Link, useLocation, useNavigate, useParams } from "react-router-dom";
import Image from "../../../elements/Image";
import Badge from "../../../elements/Badge";
import dayjs from "dayjs";
import { Swiper, SwiperSlide } from "swiper/react";
import { Autoplay, Pagination } from "swiper/modules";
import Drawer from "../../../drawer/Drawer";
import { useEffect, useState } from "react";
import TicketInfo from "../tickets-order/TicketInfo";
import Ticket from "../tickets-order/Ticket";
import { useMutation, useQuery } from "@tanstack/react-query";
import { orderDetails } from "../../../../services/orderService";
import { chatCreate } from "../../../../services/chatService";
import { useSelector } from "react-redux";
import { toast } from "react-toastify";
import TicketOrderDetailsDrawer from "../tickets-order/TicketOrderDetailsDrawer";
import { t } from "i18next";

const TicketsOrderItem = ({ data }) => {
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();
  // const { orderId } = useParams();
  const { lang } = useSelector((state) => state?.langProvider);
  const location = useLocation();
  const queryObject = Object.fromEntries(new URLSearchParams(location.search));
  const { orderId } = queryObject;

  const statusMap = {
    processing: "processing",
    completed: "success",
    failed: "danger",
    pending: "pending",
    canceled: "danger",
    cancelled: "danger",
    confirmed: "confirmed",
    "partial-refund": "partialRefund",
  };

  const statusKey = (data?.orderStatus || "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, "");

  const { data: orderData } = useQuery({
    queryKey: ["order-ticket-item", orderId],
    queryFn: async () => {
      const res = await orderDetails({
        id: orderId,
        type: "front",
      });

      if (res?.status === 200) {
        return res;
      } else {
        navigate(`/`);
      }
    },
    enabled: !!orderId,
  });

  useEffect(() => {
    if (orderId && !open && orderData?.data?._id === data?._id) {
      setOpen(true);
    }
  }, [orderId, orderData?.data, open]);

  return (
    <>
      <Link
        to={`/${lang}${data?.link}`}
        onClick={() => setOpen(true)}
        className="group relative block"
      >
        <div className="absolute inset-0 opacity-0 transition-all duration-300 pointer-events-none z-0" />
        <div className="relative z-10 flex flex-col md:flex-row gap-3 p-0 border-b border-gray-200  md:border-b-0 pb-6 md:pb-auto">
          {data?.image && (
            <div className="w-[50%] md:w-[30%] 3xl:w-[25%] aspect-[20/15] overflow-hidden shadow-sm rounded-3xl flex-shrink-0">
              <Swiper
                slidesPerView={1}
                // autoplay={{ delay: 3000 }}
                pagination={{ clickable: true }}
                modules={[Autoplay, Pagination]}
                className="w-full !h-full"
              >
                <SwiperSlide>
                  <div className="flex flex-col items-center gap-2 h-full">
                    <Image
                      src={data?.image}
                      alt=""
                      classes="w-full h-full object-cover group-hover:scale-125 transition-transform duration-300"
                    />
                  </div>
                </SwiperSlide>
              </Swiper>
            </div>
          )}
          <div className="bg-purple-50 w-full pt-7 pb-5 3xl:py-7 rounded-3xl px-4 flex flex-row-reverse border border-purple-100 gap-3 2xl:gap-3 justify-between items-start">
            {data?.startDate && data?.endDate && (
              <div className="flex flex-col items-center bg-primary/10 text-deep-purple font-semibold 2xl:rounded-lg max-w-[24%] 2xl:max-w-28 3xl:max-w-36 pt-2 overflow-hidden flex-shrink-0 text-center justify-center">
                {/* <div>
                  <i className="fa-light fa-badge-dollar"></i>
                </div> */}
                <div className="text-[11px] 2xl:text-xs uppercase text-center my-1 px-2">
                  {t("Purchase Price")}
                </div>
                <div className="text-sm 2xl:text-sm leading-none bg-primary text-white px-1 w-full text-center py-1 flex whitespace-nowrap flex justify-center items-center">
                  {data?.eventData?.currency?.symbol || "$"}{" "}
                  {parseFloat(data?.total)?.toFixed(2)}
                </div>
              </div>
            )}

            <div className="space-y-1 text-slate-800 border-r border-slate-200 2xl:pr-2 3xl:pr-6 w-full">
              <div className="text-sm text-slate-400 flex items-center gap-1 -mt-[35px] mb-4">
                {/* <span className="font-medium text-slate-600">Order Status: </span> */}
                <Badge
                  label={data?.orderStatus}
                  type={statusMap[statusKey]}
                  inferTypeFromLabel={false}
                />
              </div>
              <div className="text-lg 2xl:text-2xl font-bold leading-snug tracking-tight uppercase truncate overflow-hidden xl:w-[300px] 3xl:w-[400px]">
                {data?.title}
              </div>
              <div className="3xl:flex gap-4 items-center max-w-[250px] 2xl:max-w-[280px] 3xl:max-w-[600px] w-full !mb-3 !mt-1 space-y-2 3xl:space-y-0">
                <div className="flex items-center gap-2 border border-purple-400 rounded-4xl px-3 py-2 w-full text-center font-medium justify-center text-[11px]">
                  <i className="fa-regular fa-calendar-week"></i>
                  <span>
                    {dayjs(data?.startDate).format("DD MMM, YYYY")} -{" "}
                    {dayjs(data?.endDate).format("DD MMM, YYYY")}
                  </span>
                </div>

                <div className="flex items-center gap-2 border border-purple-400 rounded-4xl px-3 py-2 w-full text-center font-medium justify-center text-[11px]">
                  <i className="fa-regular fa-clock"></i>
                  <span>
                    {dayjs(data?.startDate).format("hh:mm A")} -{" "}
                    {dayjs(data?.endDate).format("hh:mm A")}
                  </span>
                </div>
              </div>
              <div className="text-sm text-slate-400">
                <span className="font-medium text-slate-600">
                  {t("Order No")}:{" "}
                </span>
                {data?.orderId}
              </div>
              <div className="text-sm text-slate-400">
                <span className="font-medium text-slate-600">
                  {t("Order Date")}:{" "}
                </span>
                {dayjs(data?.createdAt).format("ddd, MMM DD, hh:mm A")}
              </div>
            </div>
          </div>
        </div>
      </Link>
      <TicketOrderDetailsDrawer
        open={open}
        setOpen={setOpen}
        location={location}
        navigate={navigate}
        orderData={orderData}
      />
    </>
  );
};

export default TicketsOrderItem;
